/**
 * A quote link written the way this app used to write them.
 *
 * The quote, its revision and its option were query parameters on a static
 * path — `/quote/revisions/options?quoteId=12&revisionId=3` — and are path
 * segments now. `main.tsx` runs every incoming URL through this before the
 * router sees it, so a bookmark or a link from another system written in the
 * old shape still opens the quote it named.
 *
 * Two generations of link arrive. The Angular portal held the quote and the
 * revision in a service and put only the option's uuid in the URL, so an
 * Angular-era bookmark names an option and nothing above it. The React screens
 * that replaced it added the quote and the revision as query parameters. Both
 * are path segments now.
 *
 * There is no matrix-parameter stage here, unlike iQD Pro's shim of the same
 * name: the Angular portal never wrote one.
 */
export function normaliseLegacyPath(url: string): string {
  const [path, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  const rewritten = rewriteQuotePath(path, params);
  const search = params.toString();
  return search ? `${rewritten}?${search}` : rewritten;
}

/** A positive whole number, or null — an id that is neither names nothing. */
function idParam(params: URLSearchParams, name: string): string | null {
  const value = params.get(name);
  return value !== null && /^\d+$/.test(value) && Number(value) > 0 ? value : null;
}

/**
 * The old address of a quote screen, rewritten as the path it lives at now.
 * The parameters that became segments are taken out of the query; anything
 * else the link carried is left where it is.
 *
 * Only the exact old paths are matched, so a URL already in the new shape
 * passes through untouched. A link missing the ids it needs is left alone as
 * well: the router's catch-all sends it to the quote list, which is all a link
 * naming no quote can honestly do.
 */
function rewriteQuotePath(path: string, params: URLSearchParams): string {
  if (path === '/quote/revisions/options/view') {
    const optionId = params.get('optionId');
    // No option is the new-quote screen: this is where "New quote" went.
    if (!optionId) return '/quote/new';
    params.delete('optionId');
    const quoteId = idParam(params, 'quoteId');
    const revisionId = idParam(params, 'revisionId');
    // An Angular-era link knows the option and nothing above it.
    if (!quoteId || !revisionId) return `/quote/option/${optionId}`;
    params.delete('quoteId');
    params.delete('revisionId');
    return `/quote/${quoteId}/revision/${revisionId}/option/${optionId}`;
  }

  if (path === '/quote/revisions/options') {
    const quoteId = idParam(params, 'quoteId');
    const revisionId = idParam(params, 'revisionId');
    if (!quoteId || !revisionId) return path;
    params.delete('quoteId');
    params.delete('revisionId');
    return `/quote/${quoteId}/revision/${revisionId}`;
  }

  if (path === '/quote/revisions') {
    const quoteId = idParam(params, 'quoteId');
    if (!quoteId) return path;
    params.delete('quoteId');
    return `/quote/${quoteId}`;
  }

  return path;
}
